import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { User } from '../types';
import { DELETED_USER_REASON } from '../utils/accessBlock';

// functions.invoke devolve o corpo do erro HTTP em error.context; extrai a mensagem da edge function.
async function edgeErrorMessage(error: unknown, fallback: string): Promise<string> {
  const ctx = (error as { context?: { json?: () => Promise<{ error?: string }> } })?.context;
  if (ctx?.json) {
    try {
      const body = await ctx.json();
      if (body?.error) return body.error;
    } catch { /* mantém a mensagem padrão */ }
  }
  return fallback;
}

interface UseUsersDataOptions {
  /** Inclui usuários bloqueados (is_active=false). Só a tela de gestão de acessos precisa disso. */
  includeBlocked?: boolean;
}

export const useUsersData = (
  companyId: number | null | undefined,
  currentUser?: User | null,
  options: UseUsersDataOptions = {}
) => {
  const includeBlocked = !!options.includeBlocked;
  const queryClient = useQueryClient();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const fetchUsers = async () => {
    if (!companyId || !supabase) return [];

    // Por padrão só usuários ativos (ou que não tem a flag is_active definida ainda):
    // bloqueado não aparece em seletores de responsável, rankings etc.
    let builder = supabase
      .from('users')
      .select('*')
      .eq('company_id', companyId);
    if (!includeBlocked) builder = builder.neq('is_active', false);
    const { data, error } = await builder.order('name');

    if (error) throw error;
    // Usuário excluído (soft delete) nunca aparece, nem na tela de gestão de acessos.
    return (data || []).filter((u: User) => u.blocked_reason !== DELETED_USER_REASON);
  };

  const query = useQuery({
    queryKey: includeBlocked ? ['users', companyId, 'with-blocked'] : ['users', companyId],
    queryFn: fetchUsers,
    enabled: !!companyId,
  });

  const addUserMutation = useMutation({
    mutationFn: async (user: Omit<User, 'id' | 'created_at'> & { password?: string }) => {
      if (!companyId || !supabase) throw new Error('Configuração pendente');
      if (!user.password) throw new Error('A senha é obrigatória para criar um novo utilizador.');

      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          email: user.email,
          password: user.password,
          name: user.name,
          role: user.role,
          company_id: companyId,
        },
      });
      if (error) throw error;
      return (data as { userId?: string } | null)?.userId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', companyId] });
    },
    onError: async (err: any) => {
      console.error('Erro ao invocar a Edge Function create-user:', err);
      let detailedMessage = 'Erro ao criar utilizador. Tente novamente.';
      if (err?.context?.json && typeof err.context.json === 'function') {
        try {
          const body = await err.context.json();
          if (body?.error) {
            if (body.error.toLowerCase().includes('already registered') || body.error.toLowerCase().includes('already exists')) {
              detailedMessage = 'Este e-mail já está em uso por outro usuário no sistema.';
            } else {
              detailedMessage = body.error;
            }
          }
        } catch {}
      }
      if (detailedMessage.includes('Supabase Auth')) {
        detailedMessage = 'Este e-mail já está em uso por outro usuário no sistema.';
      }
      setErrorMsg(detailedMessage);
      throw new Error(detailedMessage);
    }
  });

  const updateUserMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<User> }) => {
      if (!supabase) throw new Error('Supabase not initialized');
      const { data, error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', companyId] });
    },
    onError: (e) => setErrorMsg('Erro ao atualizar utilizador: ' + e.message)
  });

  const deleteUserMutation = useMutation({
    mutationFn: async ({ id, transferToUserId }: { id: string; transferToUserId?: string }) => {
      if (!supabase) throw new Error('Cliente Supabase não disponível');
      const { error } = await supabase.functions.invoke('delete-user', {
        body: { userId: id, transferToUserId },
      });
      if (error) {
        throw new Error(await edgeErrorMessage(error, 'Erro ao excluir utilizador. Tente novamente.'));
      }
    },
    onSuccess: () => {
      // Leads, chats e tarefas mudam de dono junto com a exclusão.
      queryClient.invalidateQueries({ queryKey: ['users', companyId] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: (err: Error) => {
      console.error('Erro ao invocar a Edge Function delete-user:', err);
      setErrorMsg(err.message);
    }
  });

  // Bloqueio/reativação reversível feito pelo gestor. A edge function bane o
  // usuário no Auth, derruba a sessão e grava motivo/quem/quando em public.users.
  const setUserAccessMutation = useMutation({
    mutationFn: async ({ id, active, reason }: { id: string; active: boolean; reason?: string }) => {
      if (!supabase) throw new Error('Cliente Supabase não disponível');
      const { error } = await supabase.functions.invoke('toggle-user-access', {
        body: { userId: id, active, reason },
      });
      if (error) {
        throw new Error(await edgeErrorMessage(error, 'Não foi possível alterar o acesso. Tente novamente.'));
      }
    },
    onSuccess: () => {
      // Prefixo ['users', companyId] também atualiza a variante "with-blocked".
      queryClient.invalidateQueries({ queryKey: ['users', companyId] });
    },
  });

  const clearError = () => setErrorMsg(null);

  return {
    users: query.data || [],
    loading: query.isLoading,
    error: errorMsg || (query.isError ? 'Erro ao carregar utilizadores' : null),
    clearError,
    refreshData: () => query.refetch(),
    addUser: async (u: any) => await addUserMutation.mutateAsync(u),
    updateUser: async (id: string, updates: Partial<User>) => await updateUserMutation.mutateAsync({ id, updates }),
    deleteUser: async (id: string, transferToUserId?: string) => await deleteUserMutation.mutateAsync({ id, transferToUserId }),
    setUserAccess: async (id: string, active: boolean, reason?: string) =>
      await setUserAccessMutation.mutateAsync({ id, active, reason })
  };
};
